import { useState, useContext } from 'react';
import { Drawer, useTranslation, useNextgenRouter, Button, ButtonStyles, useSession } from '@nextgen-web-framework/all';
import { type AppState } from '@nextgen-web-framework/core';
import { useSelector } from 'react-redux';
import {
  myListApi,
  type InitialCartListState,
  IBundleStateWithEntry,
  type ListEntry,
  getListBundleSlot,
  ListResponse,
  getBundleRequestSlots,
  hideBV,
  ErrorAPI,
} from '@commerce-cart-webcomponents/sdk';
import { BundleComponent, CheckoutCard, type IBundleState } from '@commerce-product-webcomponents/ui';
import { type IProduct } from '@commerce-product-webcomponents/sdk';
import {
  AllCustomListContext,
  CartNotificationTheme,
  TOAST_TIMEOUT,
  useCartNotification,
  useExceptionToast,
} from '../..';
import styles from './DynamicBundleDrawer.module.scss';

export type DynamicBundleDrawerProps = {
  productData?: IProduct;
  onClose?: () => void;
  storedState?: IBundleStateWithEntry[];
  bundleEntryDetails?: ListEntry;
  onConfirmButtonClick?: (bundleState: IBundleState[]) => void;
};

export const DynamicBundleDrawer = ({
  productData,
  onClose,
  bundleEntryDetails,
  storedState = [],
  onConfirmButtonClick,
}: DynamicBundleDrawerProps) => {
  const t = useTranslation();
  const { createToast, deleteToast } = useCartNotification('dynamicBundleDrawer');
  const router = useNextgenRouter();
  const { customList, updateCustomList } = useContext(AllCustomListContext);
  const { myList }: InitialCartListState = useSelector((state: AppState) => state.commerceMyList);
  const currentList = router.params?.listId === myList?.listId ? myList : customList;
  const { isABO } = useSession();
  const [disableConfirmButton, setDisableConfirmButton] = useState(true);
  const [currentBundleState, setCurrentBundleState] = useState<IBundleState[]>([]);
  const [selected, setSelected] = useState(0);
  const drawerHeaderTitle = t('cart.DynamicBundleDrawer.drawerHeader');
  const cancelButtonTitle = t('cart.DynamicBundleDrawer.cancelButton');
  const confirmButtonTitle = t('cart.DynamicBundleDrawer.addToListButton');
  const updateButtonTitle = t('cart.DynamicBundleDrawer.update');
  const successText = t('cart.DynamicBundleDrawer.successText');
  const triggerToast = useExceptionToast();
  const minPoolQty = productData?.bundle?.slots?.reduce((acc, slot) => acc + Number(slot.maxQty), 0);
  const selectedText = t('cart.DynamicBundleDrawer.selectedText', {
    current: String(selected),
    total: String(minPoolQty),
  });

  const showSuccessToast = () => {
    createToast(
      {
        theme: CartNotificationTheme.SUCCESS,
        rightSeperator: false,
        content: successText,
        clickIcon: 'times',
        icon: 'check-circle',
        timeOut: TOAST_TIMEOUT,
      },
      deleteToast,
    );
  };

  const handleBundleProductSelected = (state: IBundleState[], enable: boolean) => {
    const totalSelectedQuantity = state.reduce((acc, data) => acc + data.quantity, 0);
    setCurrentBundleState(state);
    setSelected(totalSelectedQuantity);
    setDisableConfirmButton(!enable);
  };

  const updateListEntry = () => {
    const nonEmptyStoredState = storedState.filter(({ quantity }) => quantity);
    const nonEmptyCurrentBundleState = currentBundleState.filter(({ quantity }) => quantity);
    const updatedSlots = getListBundleSlot(nonEmptyStoredState, nonEmptyCurrentBundleState);

    const updateListPayload = {
      entry: {
        entryId: bundleEntryDetails?.entryId,
        bundleSlots: updatedSlots,
        product: {
          id: bundleEntryDetails?.product.id as string,
        },
        quantity: Number(bundleEntryDetails?.quantity),
      },
      version: Number(currentList?.version),
    };

    myListApi
      .changeQtyForEntryFromList({
        entryId: bundleEntryDetails?.entryId,
        listId: currentList?.listId,
        payload: updateListPayload,
      })
      .then((updatedList: ListResponse) => {
        updateCustomList(updatedList);
        onClose?.();
        showSuccessToast();
      })
      .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps));
  };

  const addBundleToList = () => {
    const bundleSlots = getBundleRequestSlots(currentBundleState);
    myListApi
      .addToList([
        {
          version: currentList?.version as number,
          listId: currentList?.listId as string,
          entries: [
            {
              product: {
                id: productData?.productId as string,
              },
              quantity: 1,
              bundleSlots,
            },
          ],
        },
      ])
      .then((res) => {
        updateCustomList(res.lists?.[0] ?? {});
        onClose?.();
        showSuccessToast();
      })
      .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps));
  };

  const handleConfirmButtonClick = () => {
    if (onConfirmButtonClick) {
      onConfirmButtonClick?.(currentBundleState);
      return;
    }

    if (storedState?.length) {
      updateListEntry();
    } else {
      addBundleToList();
    }
  };

  return (
    <Drawer
      closeIcon={true}
      bottomGap={0}
      onClose={onClose}
      header={drawerHeaderTitle}
      closeOnBackdropClick={true}
      footer={
        <div className={styles['dynamic-bundle-drawer-footer-buttons']}>
          <Button buttonStyle={ButtonStyles.OUTLINE} title={cancelButtonTitle} onClick={onClose} />
          <Button
            disabled={disableConfirmButton}
            title={storedState?.length ? updateButtonTitle : confirmButtonTitle}
            onClick={handleConfirmButtonClick}
          />
        </div>
      }
      useReactPortal={true}
      portalContainerId={'react-portal-drawer'}
    >
      <div className={styles['dynamic-bundle-drawer-checkout-card-container']}>
        <CheckoutCard
          className={styles['dynamic-bundle-drawer-checkout-card']}
          pointValue={isABO() ? String(productData?.pointValue) : ''}
          businessVolume={isABO() && !hideBV() ? String(productData?.businessVolume) : ''}
          productPrice={productData?.productPrice}
          productSize=""
          productId={productData?.productId as string}
          productName={productData?.productName as string}
          link={productData?.mainLink as string}
          minPrice={productData?.bundle?.minPrice}
          maxPrice={productData?.bundle?.maxPrice}
          alt={'Product Card' as string}
          brandName={productData?.brandName as string}
          isBundle={true}
        />
        <div className={styles['dynamic-bundle-drawer-checkout-card-container-text']}>{selectedText}</div>
      </div>
      <BundleComponent
        skipChecksForButton={true}
        bundle={productData?.bundle}
        onBundleProductSelected={(state, _, enable) => handleBundleProductSelected(state, enable)}
        bundleStoredState={storedState}
      />
    </Drawer>
  );
};
